import { getRequestReturnTo, withRequestReturnTo } from './requestNavigation';

export const REPORT_FILTER_KEYS = [
  'report',
  'rrBasis',
  'rrSku',
  'rrLine',
  'rrReason',
  'rrResolution',
  'rrBreakdown',
] as const;
export type RequestReportPreferences = Partial<
  Record<'startDate' | 'endDate' | (typeof REPORT_FILTER_KEYS)[number], string>
>;

export const readRequestReportPreferences = (
  storeId: string | null | undefined,
): RequestReportPreferences => {
  try {
    const saved = JSON.parse(
      sessionStorage.getItem(`request-report-dates:${storeId}`) || '{}',
    );
    return Object.fromEntries(
      ['startDate', 'endDate', ...REPORT_FILTER_KEYS]
        .filter(
          (key) =>
            typeof saved?.[key] === 'string' &&
            (key === 'startDate' || key === 'endDate'
              ? /^\d{4}-\d{2}-\d{2}$/.test(saved[key])
              : key === 'report'
              ? ['protection', 'requests', 'order-editing', 'billing'].includes(
                  saved[key],
                )
              : saved[key].length <= 250),
        )
        .map((key) => [key, saved[key]]),
    );
  } catch {
    return {};
  }
};

type NavigationLocation = { pathname: string; search: string };
const withoutReturnTo = (path: string): string => {
  const url = new URL(path, 'https://shipaid.local');
  if (!url.searchParams.has('returnTo')) return path;
  url.searchParams.delete('returnTo');
  return `${url.pathname}${url.search}${url.hash}`;
};

export const getRequestReportsHref = (
  storeId: string | null | undefined,
  location: NavigationLocation,
): string => {
  const pathname = location.pathname.replace(/\/+$/, '').toLowerCase();
  if (pathname === '/requests/reports') return `${pathname}${location.search}`;
  const preferences = readRequestReportPreferences(storeId);
  const params = new URLSearchParams();
  Object.entries(preferences).forEach(([key, value]) => {
    if (value) params.set(key, value);
  });
  const destination = `/requests/reports${
    params.toString() ? `?${params}` : ''
  }`;
  const origin = ['/requests', '/orders'].includes(pathname)
    ? `${pathname}${location.search}`
    : getRequestReturnTo(location.search, undefined, '');
  return ['/requests', '/orders'].includes(origin.split(/[?#]/)[0])
    ? withRequestReturnTo(destination, withoutReturnTo(origin))
    : destination;
};

export const getRequestsHref = (location: NavigationLocation): string => {
  const pathname = location.pathname.replace(/\/+$/, '').toLowerCase();
  if (!['/requests/reports', '/requests/billing'].includes(pathname))
    return '/requests';
  const origin = getRequestReturnTo(location.search, undefined, '/requests');
  return origin.split(/[?#]/)[0] === '/requests'
    ? withoutReturnTo(origin)
    : '/requests';
};
